/**
 * Auth Confirmation Page — scanner-safe entry point for emailed auth links.
 *
 * THE PROBLEM THIS SOLVES
 * -----------------------
 * Supabase's default `{{ .ConfirmationURL }}` points at
 * `/auth/v1/verify?token=...`, and that GET *consumes* the one-time token.
 * Corporate mail security (Microsoft Defender Safe Links, Proofpoint, Mimecast)
 * fetches every URL in an incoming message to check it is safe — so the scanner
 * spends the token before the human ever clicks, and the real user lands on a
 * login screen holding a link that was already used.
 *
 * This is not hypothetical. An invited McGee user (Studio McGee runs Exchange +
 * Defender) burned five separate invite and reset links across three days. Every
 * one produced a GoTrue `login` within ~60s of the email being sent — always
 * before the user clicked — and never a `user_updated`. From the backend the
 * account looked perfectly healthy the whole time.
 *
 * THE FIX
 * -------
 * Point the email templates at this page with `{{ .TokenHash }}` instead, and
 * redeem the token with `verifyOtp`, which is a POST issued by our own JS:
 *
 *   {{ .SiteURL }}/auth/confirm?token_hash={{ .TokenHash }}&type=invite
 *
 * A scanner's GET now lands on a static page and consumes nothing. Two layers
 * protect the token:
 *
 *   1. Redemption is a POST from client JS, so a plain URL fetch cannot do it.
 *   2. It requires a real click. We deliberately do NOT redeem on mount —
 *      some scanners render pages headlessly and would execute an on-mount
 *      effect. Headless scanners do not click buttons. One extra click is a
 *      cheap price for a link that actually survives the trip.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase/client';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import type { EmailOtpType } from '@supabase/supabase-js';

/** OTP types we accept from an email link, and where each one goes next. */
const DESTINATIONS: Record<string, { type: EmailOtpType; next: string; label: string }> = {
  invite: { type: 'invite', next: '/signup', label: 'Accept your invitation' },
  signup: { type: 'signup', next: '/signup', label: 'Confirm your account' },
  recovery: { type: 'recovery', next: '/reset-password', label: 'Reset your password' },
  magiclink: { type: 'magiclink', next: '/', label: 'Sign in' },
  email: { type: 'email', next: '/', label: 'Confirm your email' },
};

export default function ConfirmPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tokenHash = searchParams.get('token_hash');
  const rawType = searchParams.get('type') || '';
  const destination = DESTINATIONS[rawType];

  // Malformed link — missing or unrecognized params. Nothing to redeem.
  if (!tokenHash || !destination) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">Invalid link</CardTitle>
            <CardDescription className="text-center">
              This link is missing information we need. Ask your administrator to send a new one.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Link
              to="/login"
              className="text-sm text-center text-primary hover:underline flex items-center justify-center gap-2 w-full"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const handleConfirm = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: destination.type,
      });

      if (verifyError) {
        console.error('ConfirmPage: verifyOtp failed:', verifyError);
        setError(
          `${verifyError.message}. If this link is more than a few days old, ask your administrator to send a new one.`,
        );
        setIsLoading(false);
        return;
      }

      // Session is live. Send them wherever this link was meant to take them.
      navigate(destination.next, { replace: true });
    } catch (err) {
      console.error('ConfirmPage: unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <ShieldCheck className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">{destination.label}</CardTitle>
          <CardDescription className="text-center">
            Click below to continue. This confirms it is really you and not an automated email scanner.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          <Button className="w-full" onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Confirming...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
